import {
    Math as CesiumMath,
    Cartographic,
    Cesium3DTileFeature,
    Cesium3DTileset,
    Model,
    EllipsoidTerrainProvider,
    Cartesian2,
    Ellipsoid,
    Cartesian3,
    Matrix4,
    HeadingPitchRange,
    GeoJsonDataSource,
    defined
} from 'cesium';

export const getPosition = (viewer, cartesian3) => {
  let ellipsoid = viewer.scene.globe.ellipsoid;
  let cartographic = ellipsoid.cartesianToCartographic(cartesian3);
  let latitude = CesiumMath.toDegrees(cartographic.latitude);
  let longitude = CesiumMath.toDegrees(cartographic.longitude);
  return {longitude, latitude};
}

export const transformWGS84ToCartesian = (viewer, lng, lat, alt) => {
  let ellipsoid = viewer.scene.globe.ellipsoid;
  let cartographic = Cartographic.fromDegrees(lng, lat, alt);
  let cartesian3 = ellipsoid.cartographicToCartesian(cartographic);
  return cartesian3;
}

export const transformCartesianToWGS84 = (viewer, cartesian3) => {
  let ellipsoid = viewer.scene.globe.ellipsoid;
  let cartographic = ellipsoid.cartesianToCartographic(cartesian3);
  let lat = CesiumMath.toDegrees(cartographic.latitude);
  let lng = CesiumMath.toDegrees(cartographic.longitude);
  let alt = cartographic.height;
  return {
    lng: lng,
    lat: lat,
    height: alt
  }
}

/**
 * 屏幕坐标转世界坐标
 * @param position 屏幕坐标
 * @param viewer 视图对象
 *
 * @return 世界坐标
 */
export const getCartesian3FromXY = (viewer, position) => {
  if (viewer && position) {
    // 返回位于特定窗口坐标位置的所有基本体的对象列表
    const picks = viewer.scene.drillPick(position)
    let cartesian = null
    let isOn3dtiles = false
    let isOnTerrain = false
    for (let i in picks) {
      let pick = picks[i]

      if (
        (pick && pick.primitive instanceof Cesium3DTileFeature) ||
        (pick && pick.primitive instanceof Cesium3DTileset) ||
        (pick && pick.primitive instanceof Model)
      ) {
        // 存在模型数据，从模型上拾取坐标
        isOn3dtiles = true
      }

      // 3dtilset
      if (isOn3dtiles) {
        viewer.scene.pick(position)
        cartesian = viewer.scene.pickPosition(position)
        // 计算高度值
        if (cartesian) {
          // 笛卡尔坐标系转化为 wgs 坐标（弧度）
          let cartographic = Cartographic.fromCartesian(cartesian)
          if (cartographic.height < 0) cartographic.height = 0
          // 将弧度转为度
          let lon = CesiumMath.toDegrees(cartographic.longitude)
          let lat = CesiumMath.toDegrees(cartographic.latitude)
          let height = cartographic.height;
          const res = transformWGS84ToCartesian(viewer, lon, lat, height)
          if (res) {
            cartesian = res
          }
        }
      }
    }
    // 存在地形数据
    const boolTerrain = viewer.terrainProvider instanceof EllipsoidTerrainProvider
    if (!isOn3dtiles && !boolTerrain) {
      const ray = viewer.scene.camera.getPickRay(position)
      if (!ray) return null
      const res = viewer.scene.globe.pick(ray, viewer.scene)
      if (res) {
        cartesian = res
      }
      isOnTerrain = true
    }
    // 地球
    if (!isOn3dtiles && !isOnTerrain && boolTerrain) {
      const res = viewer.scene.camera.pickEllipsoid(position, viewer.scene.globe.ellipsoid)
      if (res) {
        cartesian = res
      }
    }
    if (cartesian) {
      let px = transformCartesianToWGS84(viewer, cartesian)
      if (px && px.height < 0) {
        const res = transformWGS84ToCartesian(viewer, px.lng, px.lat, px.height)
        if (res) {
          cartesian = res
        }
        return cartesian
      }
      return cartesian
    }
    return false
  }
}

export function computeCircle(radius) {
  let positions = [];
  for (let i = 0; i < 360; i++) {
    let radians = CesiumMath.toRadians(i);
    positions.push(
      new Cartesian2(
        radius * Math.cos(radians),
        radius * Math.sin(radians)
      )
    );
  }
  return positions;
}



/**
 * 添加图层
 * @param {*} viewer
 * @param {*} layConfig 图层相关配置
 */
export function addLayer(viewer, layConfig) {
  return new Promise((resolve) => {
    // 模型图层对象
    if (layConfig.type === '3dtiles' && layConfig.url) {
      // 加载自定义数据
      const tilest = new Cesium3DTileset({
        url: layConfig.url
      })
      tilest.readyPromise.then((tilests) => {
        if (tilests) {
          let surfaceNormal = Ellipsoid.WGS84.geodeticSurfaceNormal(tilests.boundingSphere.center);
          let translationb = Cartesian3.multiplyByScalar(surfaceNormal, 0, new Cartesian3());
          tilests.modelMatrix = Matrix4.fromTranslation(translationb);
          if (viewer) {
            viewer.scene.primitives.add(tilest)
            if (layConfig.show) {
              viewer.zoomTo(
                tilest,
                new HeadingPitchRange(0.0, -0.5, tilest.boundingSphere.radius * 2.0)
              )
            }
            resolve({
              type: layConfig.type ? layConfig.type : '3dtiles',
              layer: tilest,
              key: layConfig.id
            })
          }
        }
      })
    } else if (layConfig.type === 'geogson' && layConfig.url) {
      const dataSource = GeoJsonDataSource.load(layConfig.url, {
        clampToGround: true  // 贴地加载
      })
      dataSource.then((dataSource) => {
        if (viewer) {
          const areaEntities = dataSource.entities.values
          areaEntities.forEach(entity => {
            if (defined(entity.polygon)) {
              entity.name = entity.properties ? entity.properties.name : ''
            }
          })
          viewer.dataSources.add(dataSource)
          if (layConfig.show) {
            viewer.zoomTo(dataSource)
          }
          resolve({
            type: layConfig.type ? layConfig.type : 'geogson',
            layer: dataSource,
            key: layConfig.id
          })
        }
      })
    }
  })
}

export default {}
